import isEmpty from 'lodash/isEmpty';
import { getFileSize } from '../os/withFs';
import { waitForFileCompletion } from '../os/waitForFileCompletion';
import { mangleFileNameWithId } from './mimirItemFileNames';
import { logTiming } from '../log/logTiming';
import type { MakeDirectoryOptions, PathLike, Stats, WriteStream } from 'fs';
import type { Readable } from 'stream';
import { TaskUrgency } from '../async/taskPriority';
import { makePriority, PrioritySemaphore } from '../async/prioritySemaphore';
import { isFileOrDirectoryMissingError, mayBeErrnoException } from './errnoException';
import type { Logger } from '../types/logger';
import { asyncTimeout } from '../async/asyncTimeout';
import type { NodeApi } from './detectApi';
import type { ApplicationContext } from '../context/applicationContext';
import { createUuid } from './createUuid';


/**
 * IMPORTANT
 * This file gathers some functions used for file system access.
 *
 * The fs lib available via the getNodeApi at runtime is used, so that it uses the correct
 * fs available in our application running in different nodejs environments, primarily Premiere and
 * Mimir Desktop, but also can be used from Kelda and Storage Manager. It is important to get the
 * fs module at runtime and not "import from 'fs'" at compile time in order make it correctly work
 * on all systems.
 *
 * Some functions are just wrappers around the available functions in fs. There are some important
 * points to note:
 *
 * It was seen in Premiere 15.4 that promises returned by functions in fs.promises do not resolve
 * after logging out and logging back in. This is why these functions are avoided and instead, the
 * older style of util.promisify is used to make fs functions awaitable.
 *
 * In the Electron version that Mimir Desktop was developed on, some functions were missing entirely
 * on fs.promises. So those were made awaitable similarly using util.promisify.
 *
 * Since some fs.promises functions work with the newer FileHandle object instead of the classic
 * fd (file descriptor number), and we were unable to make fs.promises functions work in all situations
 * (as described above), we have had to work around using fd in some cases to use common functions
 * in this file, so that we are compatible in all systems.
 *
 * There are some quirks in Electron - some functions are totally missing when we get the runtime fs.
 * These have been implemented below. These anomalies are probably caused by the multi-process
 * architecture in Electron, where we are accessing the file system from the renderer process via a
 * "require" through the main process.
 *
 */

/**
 * For some reason, Electron strips away the functions like isFile and isDirectory from the result
 * returned with the stat callback, when this is called from the web/renderer process. So we need
 * to have a manual implementation for those by checking the mode for certain bits.
 */
function isFile(nodeApi: NodeApi, statResult: any): boolean {
  if (statResult?.isFile) {
    return statResult.isFile();
  } else if (statResult?.mode !== undefined && statResult?.mode !== null) {
    // eslint-disable-next-line no-bitwise
    const result = (BigInt(statResult?.mode) & BigInt(nodeApi.fs.constants.S_IFMT)) === BigInt(nodeApi.fs.constants.S_IFREG);
    return result;
  }

  return false;
}

export function isDirectory(nodeApi: NodeApi, statResult: any): boolean {
  if (statResult?.isDirectory) {
    return statResult.isDirectory();
  } else if (statResult?.mode !== undefined && statResult?.mode !== null) {
    // eslint-disable-next-line no-bitwise
    const result = (BigInt(statResult?.mode) & BigInt(nodeApi.fs.constants.S_IFMT)) === BigInt(nodeApi.fs.constants.S_IFDIR);
    return result;
  }

  return false;
}

export const fsStatSemaphore = new PrioritySemaphore(15);

const similateFileDelayUntil: Record<string, number> = {};

/*
  Some file systems are not atomic, meaning that even though one component
  thinks a file has been placed on the file system, another component may not
  see the file yet.

  To help test whether our code handles such cases we support simulating such
  non-atomic file systems.

  If you set `window.simulatedFsDelay = 60;` then the first doesPathExist() call
  that would have returned true will instead return false. Also, for sixty
  seconds all other calls for the same path will also return false. Only after
  60 seconds elapses will subsequent calls for the same path return the actual
  file availability.
*/

export async function doesPathExist(nodeApi: NodeApi, logger: Logger, localPath: string | null | undefined, checkAsDirectory: boolean): Promise<boolean> {
  if (!localPath || isEmpty(localPath)) {
    return false;
  }

  const statPromise = () => new Promise((resolve) => {
    nodeApi.fs.stat(localPath, (err: NodeJS.ErrnoException | null, statResult: Stats) => {
      const result = statResult && ((!checkAsDirectory && isFile(nodeApi, statResult)) || (checkAsDirectory && isDirectory(nodeApi, statResult))) || false;
      resolve(result);
    });
  }) as Promise<boolean>;

  const exists = await fsStatSemaphore.limit(
    () => logTiming(logger, () => statPromise(), `stat ${localPath}`),
    makePriority({ urgency: TaskUrgency.Default }),
    null
  );

  if (exists && typeof window === 'object') {
    const simulatedFsDelay = (window as any).simulatedFsDelay;
    if (simulatedFsDelay) {
      let hiddenUntil = similateFileDelayUntil[localPath];
      if (!hiddenUntil) {
        hiddenUntil = Date.now() + simulatedFsDelay * 1000;
        similateFileDelayUntil[localPath] = hiddenUntil;
      }

      const remaining = hiddenUntil - Date.now();
      if (remaining > 0) {
        logger.info('Pretending ' + localPath + ' is not visible for ' + remaining + ' ms more.');
        return false;
      }
    }
  }

  return exists;
}

export const isDirAvailableLocally = async (nodeApi: NodeApi, logger: Logger, localPath: string | undefined) => {
  return doesPathExist(nodeApi, logger, localPath, true);
};

export const readFile = (nodeApi: NodeApi, filePath: string) => {
  try {
    const data = nodeApi.fs.readFileSync(filePath, 'utf8');
    return data;
  } catch (err) {
    console.error(`Error reading file ${filePath}: ${err}`);
  }
};

export const readFileAsync = (nodeApi: NodeApi, filePath: string, encoding?: BufferEncoding) => new Promise<Buffer | string | undefined>((resolve, reject) => {
  nodeApi.fs.readFile(filePath, { encoding }, (err, data) => {
    if (err) {
      reject(err);
    }
    resolve(data);
  });
});

export const readFileSuppressingErrors = (nodeApi: NodeApi, filePath: string) => {
  try {
    const data = nodeApi.fs.readFileSync(filePath, 'utf8');
    return data;
  } catch (err) {
    // do nothing as function name suggests, assuming it is not critical
  }
};

export const writeFile = (nodeApi: NodeApi, filePath: any, data: any, encoding?: BufferEncoding) => {
  try {
    nodeApi.fs.writeFileSync(filePath, data, { encoding });
  } catch (err) {
    throw new Error(`Error writing file ${filePath}: ${err}`);
  }
};

export const appendFileAsync = (nodeApi: NodeApi, filePath: any, data: any, encoding?: BufferEncoding) =>  new Promise<void>((resolve, reject) => {
  try {
    nodeApi.fs.appendFile(filePath, data, { encoding }, () => resolve(undefined));
  } catch (err) {
    reject(err);
  }
});

export const writeFileAsync = (nodeApi: NodeApi, filePath: any, data: any, encoding?: BufferEncoding) => new Promise<void>((resolve, reject) => {
  nodeApi.fs.writeFile(filePath, data, { encoding }, (err) => {
    if (err) reject(`Error writing file ${filePath}: ${err}`);
    resolve(undefined);
  });
});


export function canWriteMachineLocalConfig(applicationContext: ApplicationContext): boolean {
  return applicationContext.nodeApi !== null;
}

export function dirname(nodeApi: NodeApi, p: string): string {
  return nodeApi.path.dirname(p) ?? '';
}

export function recursiveMkdirSync(nodeApi: NodeApi, dirPath: string, suppressLog = false) {
  if (!suppressLog) console.log(`recursiveMkdirSync: ${dirPath}`);
  try {
    nodeApi.fs.mkdirSync(dirPath, { recursive: true });
  } catch (ex: any) {
    if (!mayBeErrnoException(ex)) {
      throw ex;
    }

    if (ex.code !== 'EEXIST') {
      throw ex;
    }
  }
}

export async function mkdir(nodeApi: NodeApi, aPath: string, options?: MakeDirectoryOptions & {
  recursive: true;
}) {
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.mkdir(aPath, options, (err) => {
      if (err) reject(err);
      resolve();
    });
  });
}

interface DirectoryTestResult {
  isWritable: boolean;
  isLinkSupported: boolean;
  // add symlink check when needed in future
}
export const testDirWritability = async (nodeApi: NodeApi, logger: Logger, dirPath: string): Promise<DirectoryTestResult> => {
  const result = {
    isWritable: false,
    isLinkSupported: false,
  };

  if (isEmpty(dirPath)) {
    logger.info('testDirWritability: empty');
    return result;
  }
  const testFileName = `mimir-panel-writability-test-${createUuid()}.mov`;
  const testFile = nodeApi.path.join(dirPath, testFileName);
  const testTempFile = `${testFile}.tmp`;
  const testFileLink = `${testFile}.link`;

  try {
    nodeApi.fs.writeFileSync(testTempFile, 'OK');
    nodeApi.fs.renameSync(testTempFile, testFile);
    result.isWritable = true;
    result.isLinkSupported = await linkFileToDestinations(nodeApi, logger, testFile, [{ path: testFileLink }], true);
  } catch (err) {
    logger.error(`Error testing ${dirPath}`);
  }

  try {
    removeFileIfExists(nodeApi, testFile);
  } catch (err) {
    logger.error(`Error cleaning up ${testFile}`);
  }

  try {
    removeFileIfExists(nodeApi, testTempFile);
  } catch (err) {
    logger.error(`Error cleaning up ${testTempFile}`);
  }

  try {
    removeFileIfExists(nodeApi, testFileLink);
  } catch (err) {
    logger.error(`Error cleaning up ${testFileLink}`);
  }

  return result;
};

export const deleteFile = (nodeApi: NodeApi, filePath: string) => {
  try {
    nodeApi.fs.unlinkSync(filePath);
  } catch(ex) {
    console.error(`Error deleting file. Ignoring. ex: ${ex}`);
  }
};

export const deleteFileAsync = (nodeApi: NodeApi, filePath: string, suppressLog = false): Promise<void> => {
  return new Promise((resolve) => {
    try {
      nodeApi.fs.unlink(filePath, () => resolve(undefined));
    } catch(ex) {
      if (!suppressLog) console.error(`Error deleting file. Ignoring. ex: ${ex}`);
    }
  });
};

export async function waitForFileAccess(nodeApi: NodeApi, filePath: string, retryCount: number = 3) {
  let count = 0;
  while(count < retryCount) {
    try {
      const fd = nodeApi.fs.openSync(filePath, 'r');
      nodeApi.fs.closeSync(fd);
      return;
    } catch(error) {
      ++count;
    }
    await asyncTimeout(1000);
  }
  throw new Error(`Unable to open file for reading ${filePath}`);
}

export const getLocalFileSize = (nodeApi: NodeApi, filePath: string) => {
  return getFileSize(nodeApi.fs, filePath);
};

export const isFileAvailableLocally = async (nodeApi: NodeApi, logger: Logger, localPath: string | null | undefined) => {
  return doesPathExist(nodeApi, logger, localPath, false);
};


export const waitForLocalFileCompletion = async (logger: Logger, nodeApi: NodeApi, filePath: string, minSecondsToDecideGrowth: number, maxSecondsToWait: number) => {
  const sleepForOneSecond = async () => {
    await asyncTimeout(1000);
  };
  return waitForFileCompletion(logger, filePath, () => getFileSize(nodeApi.fs, filePath), sleepForOneSecond, minSecondsToDecideGrowth, maxSecondsToWait);
};


export async function rename(nodeApi: NodeApi, srcPath: string, dstPath: string) {
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.rename(srcPath, dstPath, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
}

export async function copyFile(nodeApi: NodeApi, srcPath: string, dstPath: string) {
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.copyFile(srcPath, dstPath, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
}

export function copyFileSync(nodeApi: NodeApi, srcPath: string, dstPath: string) {
  nodeApi.fs.copyFileSync(srcPath, dstPath);
}

export const removeFileIfExists = (nodeApi: NodeApi, filePath: string) => {
  if (!nodeApi.fs.existsSync(filePath)) return;

  const statResult = nodeApi.fs.statSync(filePath);
  if (!isFile(nodeApi, statResult)) return;

  nodeApi.fs.unlinkSync(filePath);
};

export const doesFileExistSync = (nodeApi: NodeApi, filePath: string) => {
  try {
    if (nodeApi.fs.existsSync(filePath)) return true;
  } catch (error) {
    // Ignoring error
  }
  return false;
};

// removes dir, if empty
export const removeDirIfExists = (nodeApi: NodeApi, dirPath: string) => {
  if (!nodeApi.fs.existsSync(dirPath)) return;

  const statResult = nodeApi.fs.statSync(dirPath);
  if (!isDirectory(nodeApi, statResult)) return;

  nodeApi.fs.rmdirSync(dirPath);
};

/**
 * This function implements recursion manually instead of using the { recusive:
 * true } option, since this code is sometimes used from the Node.js context of
 * the Premiere panel which does not support the use of rmdir options.
 */
export const removeDirAsyncRecursive = async (nodeApi: NodeApi, dirPath: string) => {
  try {
    const statResult = await stat(nodeApi, dirPath);
    if (!isDirectory(nodeApi, statResult)) return;
    await recursiveRemoveDir(nodeApi, dirPath);
  } catch (err) {
    if (isFileOrDirectoryMissingError(err)) {
      // The directory not existing is exactly what we want.
      return;
    }
    // just log
    console.error(`removeDirAsyncRecursive error: ${err}`);
  }
};

async function recursiveRemoveDir(nodeApi: NodeApi, dirPath: string) {
  const entries = await readDirAsync(nodeApi, dirPath);
  for (const name of entries) {
    const subpath = nodeApi.path.join(dirPath, name);
    const stats = await lstat(nodeApi, subpath);
    if (stats.isDirectory()) {
      await recursiveRemoveDir(nodeApi, subpath);
    } else {
      await unlink(nodeApi, subpath);
    }
  }
  await rmdir(nodeApi, dirPath);
}

export const makeDirP = (nodeApi: NodeApi, dirPath: string) => {
  try {
    nodeApi.fs.mkdirSync(dirPath, { recursive: true });
  } catch (err: any) {
    if (!mayBeErrnoException(err) || err.code !== 'EEXIST') {
      throw err;
    }
  }
};

export const makeDirPAsync = (nodeApi: NodeApi, dirPath: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      nodeApi.fs.mkdir(dirPath, { recursive: true }, () => {
        resolve();
      });
    } catch (err: any) {
      if (!mayBeErrnoException(err) || err.code !== 'EEXIST') {
        reject(err);
      }
    }
  });
};

export const encodeForFs = (pathSegment: string) => {
  if (!pathSegment) {
    return pathSegment;
  }

  // windows reserved names
  const invalidNames = ['CON', 'PRN', 'AUX', 'NUL', 'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
  'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'];

  // if one of the reserved names, replace with the percent-coded string
  const psUpperCase = pathSegment.toUpperCase();
  if (invalidNames.includes(psUpperCase)) {
    let result = '';
    for (let i = 0; i < psUpperCase.length; i++) {
      const charCodeHex = psUpperCase.charCodeAt(i).toString(16).toUpperCase();
      result = `${result}%${charCodeHex}`;
    }

    return result;
  }

  // encode special characters, starting with percent itself
  pathSegment = pathSegment.replace(/%/g, '%25');

  // then the other characters which are not allowed in file systems, mostly windows
  pathSegment = pathSegment.replace(/</g, '%3C');
  pathSegment = pathSegment.replace(/>/g, '%3E');
  pathSegment = pathSegment.replace(/:/g, '%3A');
  pathSegment = pathSegment.replace(/"/g, '%22');
  pathSegment = pathSegment.replace(/\//g, '%2F');
  pathSegment = pathSegment.replace(/\\/g, '%5C');
  pathSegment = pathSegment.replace(/\|/g, '%7C');
  pathSegment = pathSegment.replace(/\?/g, '%3F');
  pathSegment = pathSegment.replace(/\*/g, '%2A');

  // also on windows, it must not end with dot or space. replace with percent code if it does.
  const lastChar = pathSegment[pathSegment.length - 1];
  if (lastChar === '.' || lastChar === ' ') {
    const charCodeHex = lastChar.charCodeAt(0).toString(16).toUpperCase();
    pathSegment = `${pathSegment.substr(0, pathSegment.length - 1)}%${charCodeHex}`;
  }

  return pathSegment;
};

export async function linkAsync(nodeApi: NodeApi, filePath: string, linkPath: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.link(filePath, linkPath, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
}

export async function createRelativeSymlink(nodeApi: NodeApi, target: string, link: string): Promise<void> {
  // path.relative() works on paths not filenames, so let's find the
  // relative path to go from the directory where the symbolic link
  // will be to the directory where the actual file is
  const relativeTargetPath = nodeApi.path.relative(nodeApi.path.dirname(link), nodeApi.path.dirname(target));

  // Let's append to this path the target's file name and we have our link
  // like ../../../../d816feac-745b-47fa-b824-6491f1797bbc.png
  const relativeTarget = `${relativeTargetPath}/${nodeApi.path.basename(target)}`;
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.symlink(relativeTarget, link, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
}

export interface ItemFile {
  path: string;
  itemId?: string;
}

/**
 * This function can create multiple links to a single source file. When a destination contains
 * itemId and the destination path already exists, then it will append the id to the filename to
 * make it unique and not clash with the existing file.
 *
 * @param filePath the source file
 * @param destinations the destination paths where new links are to be created
 * @param useHardLink
 */
export const linkFileToDestinations = async (nodeApi: NodeApi, logger: Logger, filePath: string, destinations: ItemFile[], useHardLink: boolean = false) => {
  if (!await isFileAvailableLocally(nodeApi, logger, filePath)) throw new Error(`File does not exist: ${filePath}`);
  let succeeded = true;
  try {
    for (const destination of destinations) {
      let linkPath = destination.path;
      makeDirP(nodeApi, nodeApi.path.dirname(linkPath));
      const fileExistsAtDestination = await isFileAvailableLocally(nodeApi, logger, linkPath);
      if (fileExistsAtDestination && destination.itemId) {
        // alternate name
        linkPath = mangleFileNameWithId(linkPath, destination.itemId);
      }

      removeFileIfExists(nodeApi, linkPath);
      if (useHardLink) {
        await linkAsync(nodeApi, filePath, linkPath);
      } else {
        await createRelativeSymlink(nodeApi, filePath, linkPath);
      }
    }
  } catch (err) {
    succeeded = false;
    logger.error(`Error in linkFileToDestinations (useHardLink = ${useHardLink}): ${err}`);
  }

  return succeeded;
};

async function statAndHandleEnoent(nodeApi: NodeApi, filePath: string): Promise<Stats | 'fileMissing'> {
  return new Promise((resolve, reject) => {
    nodeApi.fs.stat(filePath, (err, result) => {
      if (err) {
        if (isFileOrDirectoryMissingError(err)) return resolve('fileMissing');
        reject(err);
      }
      resolve(result);
    });
  });
}

async function compareSourceAndDestination(nodeApi: NodeApi, sourcePath: string, destinationPath: string, logger: Logger): Promise<'destinationSizeMismatch' | 'sourceMissing' | 'destinationMissing' | 'destinationMatchesSource'> {
  const [sourceStat, destinationStat] = await Promise.all([
    statAndHandleEnoent(nodeApi, sourcePath),
    statAndHandleEnoent(nodeApi, destinationPath)
  ]);

  if (sourceStat === 'fileMissing') return 'sourceMissing';
  if (destinationStat === 'fileMissing') return 'destinationMissing';

  const isSourceAFile = isFile(nodeApi, sourceStat);
  logger.info(`Source stats -> path: ${sourcePath}, size: ${sourceStat.size}, isFile: ${isSourceAFile}`);
  const isDestinationAFile = isFile(nodeApi, destinationStat);
  logger.info(`Destination stats -> path: ${destinationPath}, size: ${destinationStat.size}, isFile: ${isDestinationAFile}`);

  if (!isSourceAFile) return 'sourceMissing';
  if (!isDestinationAFile) return 'destinationMissing';

  if (sourceStat.size !== destinationStat.size) return 'destinationSizeMismatch';

  return 'destinationMatchesSource';
}

async function hardLinkToDestination(nodeApi: NodeApi, logger: Logger, filePath: string, destination: string): Promise<'linkSuccessful' | 'linkFailed'> {
  try {
    await linkAsync(nodeApi, filePath, destination);
    return 'linkSuccessful';
  } catch (error) {
    logger.error(`Error hard linking ${filePath} to ${destination}: ${error}`);
    return 'linkFailed';
  }
}

/**
 * Given a source and destination path, creates hard link or performs copy if hard linking
 * fails. If a file already exists at the destination path, compares the size with source
 * and if size mismatches skips the whole operation. If size does not match, decides whether
 * to overwrite or not depending on the 'overwriteIfFileExistsAndSizeMismatch' parameter.
 * @param overwriteIfFileExistsAndSizeMismatch Whether to skip operation or overwrite destination if
 * a file is available at destination and size does not match.
 */
export const hardLinkOrCopy = async (
  nodeApi: NodeApi,
  logger: Logger,
  filePath: string,
  destination: string,
  overwriteIfFileExistsAndSizeMismatch: 'skip' | 'overwrite',
  copyFunction: (nodeApi: NodeApi, sourcePath: string, destinationPath: string) => Promise<void>
) => {
  if (filePath === destination) {
    logger.info(`Source and destination paths are same (${filePath}). Skipping hard link/copy`);
    return;
  }
  const comparison = await compareSourceAndDestination(nodeApi, filePath, destination, logger);
  logger.info(`compareSourceAndDestination result (${filePath} -> ${destination}): ${comparison}`);

  if (comparison === 'destinationMatchesSource' || comparison === 'sourceMissing') return;
  if (comparison === 'destinationSizeMismatch' && overwriteIfFileExistsAndSizeMismatch === 'skip') {
    logger.info(`File already exists at destination: ${destination} but size does not match source. Skipping overwriting because of overwrite flag: "${overwriteIfFileExistsAndSizeMismatch}"`);
    return;
  }

  let linkResult: 'cannotLinkAsDestinationExists' | 'linkSuccessful' | 'linkFailed' = 'cannotLinkAsDestinationExists';

  // As hard linking always fails if a file exists at destination, only
  // attempt hard-link if destination is missing
  if (comparison === 'destinationMissing') {
    linkResult = await hardLinkToDestination(nodeApi, logger, filePath, destination);
    logger.info(`hardLinkToDestination result (${filePath} -> ${destination}): ${linkResult}`);
  }

  if (linkResult === 'cannotLinkAsDestinationExists' || linkResult === 'linkFailed') {
    logger.info(`Fall back to copy ${filePath} to ${destination}`);
    await copyFunction(nodeApi, filePath, destination);
  }
};


export async function getPipePromise(source: Readable, destination: WriteStream): Promise<void> {
  const promise = new Promise<void>((resolve, reject) => {
    source.on('error', (err) => reject(err));
    destination.on('finish', () => resolve());
    destination.on('error', (err) => reject(err));
  });
  source.pipe(destination);
  return promise;
}

export const getFileExtension = (nodeApi: NodeApi, filePath: string) => {
  return nodeApi.path.extname(filePath);
};

export const open = async (nodeApi: NodeApi, filePath: PathLike, flags: string | number, mode?: string | number | undefined) => {
  // In Electron, though fs.promises.open works, but it returns a FileHandle object instead of a number.
  // But other functions expect a number as fd, so we are using the classic version and promisifying it.
  return new Promise<number>((resolve, reject) => {
    nodeApi.fs.open(filePath, flags, mode, (err, fd) => {
      if (err) {
        reject(err);
      }
      resolve(fd);
    });
  });
};

export const close = async (nodeApi: NodeApi, fd: number) => {
  // In Electron, though fs.promises.close is not defined, so we are using the classic version and promisifying it.
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.close(fd, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
};

export async function write(
  nodeApi: NodeApi,
  fd: number,
  buffer: Uint8Array,
  offset?: number,
  length?: number,
  position?: number | null
): Promise<{ bytesWritten: number, buffer: Uint8Array }> {
  // util.promisify cannot return more than one arg from the callback. so we need a manually built promise.
  return new Promise((resolve, reject) => {
    try {
      nodeApi.fs.write(fd, buffer, offset, length, position, (err, bytesWritten, bufferAgain) => {
        if (err) {
          reject(err);
        } else {
          resolve({
            bytesWritten,
            buffer: bufferAgain,
          });
        }
      });
    } catch(err) {
      console.log(`write err: ${err}`);
      reject(err);
    }
  });
}

export const getBasenameWithoutExtension = (nodeApi: NodeApi, filePath: string) => {
  return nodeApi.path.basename(filePath, getFileExtension(nodeApi, filePath));
};

export const fsync = async (nodeApi: NodeApi, fd: number) => {
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.fsync(fd, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
};

export async function read(nodeApi: NodeApi, fd: number, buffer: Uint8Array, offset: number, length: number, position: number | null): Promise<{ bytesRead: number, buffer: Uint8Array }> {
  // util.promisify cannot return more than one arg from the callback. so we need a manually built promise.
  // if a zero-length read is requested, fs.read never gets back after logout+login in Premiere 15.4. So we
  // return here immediately.
  if (length === 0) {
    return {
      bytesRead: 0,
      buffer,
    };
  }

  return new Promise((resolve, reject) => {
    try {
      nodeApi.fs.read(fd, buffer, offset, length, position, (err, bytesRead, bufferAgain) => {
        if (err) {
          reject(err);
        } else {
          resolve({
            bytesRead,
            buffer: bufferAgain,
          });
        }
      });
    } catch(err) {
      console.log(`read err: ${err}`);
      reject(err);
    }
  });
}

export const fstat = async (nodeApi: NodeApi, fd: number) => {
  return new Promise<Stats>((resolve, reject) => {
    nodeApi.fs.fstat(fd, (err, stats) => {
      if (err) {
        reject(err);
      }
      resolve(stats);
    });
  });
};

export const lstat = async (nodeApi: NodeApi, path_: string) => {
  return new Promise<Stats>((resolve, reject) => {
    nodeApi.fs.lstat(path_, (err, stats) => {
      if (err) {
        reject(err);
      }
      resolve(stats);
    });
  });
};

export const rmdir = async (nodeApi: NodeApi, dirPath: string) => {
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.rmdir(dirPath, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
};

export const unlink = async (nodeApi: NodeApi, filePath: string) => {
  return new Promise<void>((resolve, reject) => {
    nodeApi.fs.unlink(filePath, (err) => {
      if (err) {
        reject(err);
      }
      resolve();
    });
  });
};

export const stat = async (nodeApi: NodeApi, filePath: string) => {
  return new Promise<Stats>((resolve, reject) => {
    nodeApi.fs.stat(filePath, (err, stats) => {
      if (err) {
        reject(err);
      }
      resolve(stats);
    });
  });
};

export const join = (nodeApi: NodeApi, ...paths: string[]) => {
  return nodeApi.path.join(...paths);
};

export const normalize = (nodeApi: NodeApi, path: string) => {
  return nodeApi.path.normalize(path);
};

export const readDirAsync = (nodeApi: NodeApi, dirPath: string) => {
  return new Promise<string[]>((resolve, reject) => {
    nodeApi.fs.readdir(dirPath, undefined, (err, files) => {
      if (err) reject(err);
      resolve(files);
    });
  });
};

export const largestFileWithPrefixInDir = async (nodeApi: NodeApi, dirPath: string, prefix: string) => {
  const fileNames = (await readDirAsync(nodeApi, dirPath)).filter(name => name.startsWith(prefix));
  if (fileNames.length === 0) {
    return null;
  } else if (fileNames.length === 1) {
    return fileNames[0];
  }

  // It looks like there are multiple files with the same prefix.
  // We need to find the largest one by looking at their sizes.
  let largestSize = 0;
  let largestFileName = fileNames[0];
  for (const name of fileNames) {
    const fullFilePath = nodeApi.path.join(dirPath, name);
    const statResult = await stat(nodeApi, fullFilePath);
    if (largestSize < statResult.size) {
      largestSize = statResult.size;
      largestFileName = name;
    }
  }

  return largestFileName;
};

export const createReadStream = (nodeApi: NodeApi, pathLike: PathLike, options?: {
    flags?: string;
    encoding?: BufferEncoding;
    fd?: number;
    mode?: number;
    autoClose?: boolean;
    emitClose?: boolean;
    start?: number;
    end?: number;
    highWaterMark?: number;
  }) => {
  return nodeApi.fs.createReadStream(pathLike, options);
};


export const createWriteStream = (nodeApi: NodeApi, pathLike: PathLike, options?: {
    flags?: string;
    encoding?: BufferEncoding;
    fd?: number;
    mode?: number;
    autoClose?: boolean;
    emitClose?: boolean;
    start?: number;
    highWaterMark?: number;
  }) => {
  return nodeApi.fs.createWriteStream(pathLike, options);
};

export const removeFileSuppressingErrors = async (nodeApi: NodeApi, filePath: string) => {
  try {
    await unlink(nodeApi, filePath);
  } catch(err) {
    // suppress
  }
};

interface RemoveFileOptions {
  maxRetries: number;
  retryDelay: number;
  suppressLogs: boolean;
}
export const removeFileWithRetry = async (nodeApi: NodeApi, logger: Logger, filePath: string, options: RemoveFileOptions ) => {
  const { maxRetries, retryDelay, suppressLogs } = options;
  let attempts = 0;
  while (attempts <= maxRetries) {
    try {
      await unlink(nodeApi, filePath);
      return;
    } catch (err: any) {
      if (err.code === 'EBUSY' && attempts < maxRetries) {
        if (!suppressLogs) {
          logger.warn(`EBUSY: Retrying deletion of ${filePath} in ${retryDelay} ms...`);
        }
        await asyncTimeout(retryDelay);
        attempts++;
      } else {
        if (!suppressLogs) {
          logger.error(`Failed to delete ${filePath}: ${err}`);
        }
        return;
      }
    }
  }
};
